// Simple adapter for hitting logs (placeholder data structure)
import { env } from '../lib/env.js';
import { getMockData } from '../lib/apiClient.js';
// Utilities to load and process data/logs/hitters-*.js
// Provides: loadAllHittingLogs(), listHitters(), getHitterRows(name, date?), summarizeSnapshot(name, date?)

// Webpack/CRA-friendly dynamic import context - loading from backup/logs with real Thunder hitter names
const ctx = require.context('../backup/logs', false, /^\.\/hitters-\d{4}-\d{2}-\d{2}\.js$/);

function num(v) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
}

function pick(obj, keys) {
  for (const k of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
  }
  return undefined;
}

// Flatten a hitters module export into per-AB rows
function flattenModule(mod, dateGuess) {
  const root = mod && (mod.default ?? mod);
  const items = [];

  // Primary shape in this repo: default export is an array of { hitter, atBats: [...] }
  if (Array.isArray(root)) {
    for (const h of root) {
      const hitterName = String(pick(h, ['hitter', 'Hitter', 'name', 'player']) || '').trim();
      const atBats = Array.isArray(h?.atBats) ? h.atBats : Array.isArray(h?.events) ? h.events : [];
      for (const ab of atBats) {
        items.push({
          date: dateGuess || null,
          hitter: hitterName,
          inning: pick(ab, ['inning', 'Inning', 'ip']) ?? null,
          result: pick(ab, ['result', 'Result']) || '',
          ev: num(pick(ab, ['ev', 'EV', 'exitVelo', 'exit_velocity'])),
          la: num(pick(ab, ['la', 'LA', 'launchAngle', 'launch_angle'])),
          distance: num(pick(ab, ['distance', 'Distance', 'dist', 'Dst'])),
          // optional location fields if site provides them for batted ball
          px: num(pick(ab, ['px', 'plate_x', 'x'])),
          pz: num(pick(ab, ['pz', 'plate_z', 'z'])),
          zone: num(pick(ab, ['zone', 'Zone'])),
          // optional pass-throughs if needed later
          pitchType: pick(ab, ['pitchType', 'PitchType', 'pitch']) || null,
          spinRate: num(pick(ab, ['spinRate', 'SpinRate', 'spin'])) ,
          pitchHeight: num(pick(ab, ['pitchHeight', 'PitchHeight', 'height'])),
        });
      }
    }
    return items;
  }

  // Fallbacks: if a different shape is ever introduced
  const candidates = [];
  if (Array.isArray(root?.rows)) candidates.push(root.rows);
  if (Array.isArray(root?.events)) candidates.push(root.events);
  for (const v of Object.values(root || {})) {
    if (Array.isArray(v)) candidates.push(v);
  }
  for (const arr of candidates) {
    for (const r of arr) {
      items.push({
        date: dateGuess || null,
        hitter: String(pick(r, ['hitter', 'Hitter', 'name', 'player']) || '').trim(),
        inning: pick(r, ['inning', 'Inning', 'ip']) ?? null,
        result: pick(r, ['result', 'Result']) || '',
        ev: num(pick(r, ['ev', 'EV', 'exitVelo', 'exit_velocity'])),
        la: num(pick(r, ['la', 'LA', 'launchAngle', 'launch_angle'])),
        distance: num(pick(r, ['distance', 'Distance', 'dist', 'Dst'])),
        px: num(pick(r, ['px', 'plate_x', 'x'])),
        pz: num(pick(r, ['pz', 'plate_z', 'z'])),
        zone: num(pick(r, ['zone', 'Zone'])),
      });
    }
  }
  return items;
}

export function loadAllHittingLogs() {
  const rows = [];
  ctx.keys().forEach((key) => {
    // key like "./hitters-2025-06-04.js"
    const m = key.match(/hitters-(\d{4}-\d{2}-\d{2})/);
    const dateStr = m ? m[1] : null;
    let mod;
    try {
      mod = ctx(key);
    } catch (e) {
      return; // skip broken module
    }
    const items = flattenModule(mod, dateStr);
    rows.push(...items);
  });
  return rows;
}

// Cache all rows at module load for quick list operations
const _ALL = loadAllHittingLogs();

export function listHitters() {
  const all = loadAllHittingLogs();
  const names = [...new Set(all.map(r => r.hitter))].filter(Boolean).sort();
  return names;
}

export function getHitterRows(name, date = null) {
  const n = String(name || '').trim().toLowerCase();
  return _ALL.filter((r) => (!n || r.hitter.toLowerCase() === n) && (!date || r.date === date));
}

// --- Hitter PD Summary ---
export function summarizeHitterPD(hitterName) {
  const rows = getHitterRows(hitterName);
  if (!rows.length) return null;
  const ev = rows.map((r) => r.ev).filter((v) => typeof v === 'number');
  const la = rows.map((r) => r.la).filter((v) => typeof v === 'number');
  const hh = ev.filter((v) => v >= 95).length;
  return {
    evAvg: ev.length ? (ev.reduce((a, b) => a + b, 0) / ev.length).toFixed(1) : "-",
    laAvg: la.length ? (la.reduce((a, b) => a + b, 0) / la.length).toFixed(1) : "-",
    hhPct: ev.length ? ((hh / ev.length) * 100).toFixed(1) : "0.0",
    sample: rows.length,
  };
}

export function summarizeSnapshot(name, date = null) {
  const rows = getHitterRows(name, date);
  const sample = rows.length;
  const evs = rows.map((r) => r.ev).filter((v) => typeof v === 'number');
  const las = rows.map((r) => r.la).filter((v) => typeof v === 'number');
  const avg = (a) => (a.length ? Math.round((a.reduce((x, y) => x + y, 0) / a.length) * 10) / 10 : 0);
  const hardHit = rows.filter((r) => (r.ev ?? 0) >= 95).length;
  const barrels = rows.filter((r) => (r.ev ?? 0) >= 98 && (r.la ?? 0) >= 10 && (r.la ?? 0) <= 30).length;
  const hhPct = sample ? Math.round((hardHit / sample) * 100) : 0;
  return { sample, avgEV: avg(evs), avgLA: avg(las), hardHit, hhPct, barrels, hasData: sample > 0 };
}
